import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Alert,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

export default function ImpersonateUserTab({
  listUsers,
  apiImpersonate,
  signInWithCustomTokenAuth,
  navigate,
  platformOwnerUserId,
  currentUserId,
}) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);
  const [error, setError] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  const isPlatformUser = currentUserId === platformOwnerUserId;

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadUsers = useCallback(
    async (searchTerm = "") => {
      if (!isPlatformUser) return;
      setLoading(true);
      setError("");
      try {
        const data = await listUsers(searchTerm);
        setUsers(Array.isArray(data) ? data : []);
        setSelectedUser(null);
      } catch (err) {
        setError(err?.message || "Failed to load users");
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [listUsers, isPlatformUser],
  );

  useEffect(() => {
    if (isPlatformUser) {
      loadUsers(debouncedSearch);
    }
  }, [isPlatformUser, debouncedSearch, loadUsers]);

  const handleImpersonate = async () => {
    if (!selectedUser?.id) return;
    setImpersonating(true);
    setError("");
    try {
      const { customToken } = await apiImpersonate(selectedUser.id);
      await signInWithCustomTokenAuth(customToken);
      localStorage.removeItem("userInfo");
      navigate(selectedUser.isCoach ? "/clients" : "/workouts", {
        replace: true,
      });
      window.location.reload();
    } catch (err) {
      setError(err?.message || "Impersonation failed");
    } finally {
      setImpersonating(false);
    }
  };

  if (!isPlatformUser) {
    return (
      <Alert severity="error">You are not authorized to use this page.</Alert>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Search for a user and sign in as them. Only platform users can access
        this.
      </Typography>

      <TextField
        fullWidth
        size="small"
        placeholder="Search by name, email, or ID..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <List
            dense
            sx={{
              maxHeight: 400,
              overflow: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            {users.length === 0 ? (
              <ListItem>
                <ListItemText primary="No users found." />
              </ListItem>
            ) : (
              users.map((user) => (
                <ListItem key={user.id} disablePadding>
                  <ListItemButton
                    selected={selectedUser?.id === user.id}
                    onClick={() => setSelectedUser(user)}
                  >
                    <ListItemText
                      primary={user.name || user.email || user.id}
                      secondary={
                        <>
                          {user.email && `${user.email} · `}
                          {user.isCoach ? "Coach" : "Client"}
                          {user.id && ` · ${user.id}`}
                        </>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))
            )}
          </List>

          <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              disabled={!selectedUser || impersonating}
              onClick={handleImpersonate}
            >
              {impersonating ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Signing in…
                </>
              ) : (
                "Impersonate"
              )}
            </Button>
          </Box>
        </>
      )}
    </>
  );
}
